import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiGrid, FiDollarSign, FiCalendar, FiSearch, FiUser, FiFileText } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, AreaChart, Area } from 'recharts';
import styles from './AdminDashboard.module.css';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Drawer from '../../components/Drawer/Drawer';
import drawerStyles from '../../components/Drawer/Drawer.module.css';
import Pagination from '../../components/Pagination/Pagination';
import { useCurrency } from '../../app/CurrencyContext';
import adminPanelStyles from '../../styles/adminPanels.module.css';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchAllLeads, updateLeadStatus } from '../../features/leads/leadsSlice';
import { fetchAdminStats } from '../../features/users/usersSlice';
import { useTheme } from '../../app/ThemeContext';
import { LeadStatus, LeadType, Lead } from '../../types';

const STATUS_FILTERS: { label: string; value: LeadStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Rejected', value: 'rejected' },
];

const TYPE_TABS: { label: string; value: LeadType | 'all'; Icon: React.ComponentType<any> }[] = [
  { label: 'All requests', value: 'all', Icon: FiGrid },
  { label: 'Investment leads', value: 'invest', Icon: FiDollarSign },
  { label: 'Meeting requests', value: 'meeting', Icon: FiCalendar },
];

const PAGE_SIZE = 8;

const AdminDashboard: React.FC = () => {
  const { formatFromInr, formatTransaction } = useCurrency();
  const dispatch = useAppDispatch();
  const { allLeads, status } = useAppSelector((state) => state.leads);
  const { stats } = useAppSelector((state) => state.users);
  const { theme } = useTheme();
  const [activeType, setActiveType] = useState<LeadType | 'all'>('all');
  const [activeFilter, setActiveFilter] = useState<LeadStatus | 'all'>('all');
  const [range, setRange] = useState<'day' | 'week' | 'month'>('month');
  const [tableSearch, setTableSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Lead | null>(null);
  const [contactBy, setContactBy] = useState('');

  useEffect(() => {
    dispatch(fetchAdminStats());
  }, [dispatch]);

  useEffect(() => {
    const filters: { status?: string; leadType?: string } = {};
    if (activeFilter !== 'all') filters.status = activeFilter;
    if (activeType !== 'all') filters.leadType = activeType;
    dispatch(fetchAllLeads(filters));
    setPage(1);
  }, [dispatch, activeFilter, activeType]);

  const handleConfirm = () => {
    if (!selected) return;
    if (!contactBy) {
      toast.warning('Please pick a "contact by" date & time before confirming.');
      return;
    }
    dispatch(updateLeadStatus({ id: selected.id, status: 'confirmed', contactBy: new Date(contactBy).toISOString() }));
    toast.success('Request confirmed.');
    setSelected(null);
    setContactBy('');
  };

  const handleReject = () => {
    if (!selected) return;
    dispatch(updateLeadStatus({ id: selected.id, status: 'rejected' }));
    toast.info('Request rejected.');
    setSelected(null);
  };

  const pending = stats?.pendingLeads || 0;
  const confirmed = stats?.confirmedLeads || 0;
  const rejected = Math.max((stats?.totalLeads || 0) - pending - confirmed, 0);
  const chartData = [
    { name: 'Pending', value: pending, color: '#c9793f' },
    { name: 'Confirmed', value: confirmed, color: '#1d60f6' },
    { name: 'Rejected', value: rejected, color: '#e2534a' },
  ];

  const rangeDays = range === 'day' ? 1 : range === 'week' ? 7 : 30;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - rangeDays);
  const rangedLeads = allLeads.filter((l) => new Date(l.created_at) >= cutoff);
  const sortedLeads = [...allLeads].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const areaData = sortedLeads.map((l, i) => ({
    date: new Date(l.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    total: i + 1,
  }));

  const filteredLeads = allLeads.filter((l) =>
    !tableSearch ||
    l.full_name.toLowerCase().includes(tableSearch.toLowerCase()) ||
    l.email.toLowerCase().includes(tableSearch.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / PAGE_SIZE));
  const pagedLeads = filteredLeads.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AdminShell>
      <PageHeader eyebrow="Super admin" title="All leads & requests" action={<AdminWalletBadge />} />
      <div className={styles.page}>
        {stats && (
          <div className={styles.heroCard}>
            <div className={styles.heroTop}>
              <div>
                <div className={styles.heroLabel}>Total requests</div>
                <div className={styles.heroValue}>{stats.totalLeads}</div>
                <div className={styles.heroSub}>{rangedLeads.length} new in the last {range}</div>
              </div>
              <div className={styles.rangeToggle}>
                {(['day', 'week', 'month'] as const).map((r) => (
                  <button key={r} className={range === r ? styles.rangeActive : ''} onClick={() => setRange(r)}>
                    {r[0].toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={areaData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="adminHeroFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1d60f6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#1d60f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'light' ? 'rgba(15,23,42,.08)' : 'rgba(255,255,255,.06)'} vertical={false} />
                <XAxis dataKey="date" stroke={theme === 'light' ? '#4b5768' : '#8391a3'} fontSize={11} tickLine={false} axisLine={false} />
                <YAxis hide />
                <Tooltip contentStyle={{ background: theme === 'light' ? '#ffffff' : '#0d1420', border: theme === 'light' ? '1px solid rgba(15,23,42,.12)' : '1px solid rgba(255,255,255,.12)', borderRadius: 10, color: theme === 'light' ? '#0f1420' : '#eaf2f8' }} />
                <Area type="monotone" dataKey="total" stroke="#1d60f6" strokeWidth={2} fill="url(#adminHeroFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {stats && (
          <div className={styles.statsGrid}>
            <div className={styles.statCard} style={{ animationDelay: '0s' }}>
              <div className={styles.statValue}>{stats.totalLeads}</div>
              <div className={styles.statLabel}>Total requests</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.05s' }}>
              <div className={styles.statValue}>{stats.totalUsers}</div>
              <div className={styles.statLabel}>Registered users</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.1s' }}>
              <div className={styles.statValue}>{stats.pendingLeads}</div>
              <div className={styles.statLabel}>Pending</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.15s' }}>
              <div className={styles.statValue}>{stats.confirmedLeads}</div>
              <div className={styles.statLabel}>Confirmed</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.2s' }}>
              <div className={styles.statValue}>{formatFromInr(stats.totalDepositVolume ?? 0)}</div>
              <div className={styles.statLabel}>Approved deposit volume</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.25s' }}>
              <div className={styles.statValue}>{stats.pendingPayments ?? 0}</div>
              <div className={styles.statLabel}>Pending payments</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.3s' }}>
              <div className={styles.statValue}>{stats.pendingWithdrawals ?? 0}</div>
              <div className={styles.statLabel}>Pending withdrawals</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.35s' }}>
              <div className={styles.statValue}>{stats.pendingPartnerApplications ?? 0}</div>
              <div className={styles.statLabel}>Partner applications</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.4s' }}>
              <div className={styles.statValue}>{formatFromInr(stats.totalRewardsClaimed ?? 0)}</div>
              <div className={styles.statLabel}>Rewards claimed</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.45s' }}>
              <div className={styles.statValue}>{formatFromInr(stats.totalPortalInvested ?? 0)}</div>
              <div className={styles.statLabel}>Portal A/B/C invested ({stats.totalPortalInvestments ?? 0})</div>
            </div>
            <div className={styles.statCard} style={{ animationDelay: '.5s' }}>
              <div className={styles.statValue}>{formatFromInr(stats.totalPortalPaidOut ?? 0)}</div>
              <div className={styles.statLabel}>Portal payouts paid out</div>
            </div>
            <Link to="/admin/kyc" className={styles.statCard} style={{ animationDelay: '.55s', display: 'block', textDecoration: 'none', color: 'inherit' }}>
              <div className={styles.statValue}>{stats.pendingKyc ?? 0}</div>
              <div className={styles.statLabel}>Pending KYC review</div>
            </Link>
            <Link to="/admin/kyb" className={styles.statCard} style={{ animationDelay: '.6s', display: 'block', textDecoration: 'none', color: 'inherit' }}>
              <div className={styles.statValue}>{stats.pendingKyb ?? 0}</div>
              <div className={styles.statLabel}>Pending KYB review</div>
            </Link>
          </div>
        )}

        {stats && (
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Requests by status</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'light' ? 'rgba(15,23,42,.1)' : 'rgba(255,255,255,.08)'} vertical={false} />
                <XAxis dataKey="name" stroke={theme === 'light' ? '#4b5768' : '#8391a3'} fontSize={12} tickLine={false} axisLine={{ stroke: theme === 'light' ? 'rgba(15,23,42,.12)' : 'rgba(255,255,255,.1)' }} />
                <YAxis stroke={theme === 'light' ? '#4b5768' : '#8391a3'} fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: theme === 'light' ? '#ffffff' : '#0d1420',
                    border: theme === 'light' ? '1px solid rgba(15,23,42,.12)' : '1px solid rgba(255,255,255,.12)',
                    borderRadius: 10,
                    color: theme === 'light' ? '#0f1420' : '#eaf2f8',
                  }}
                  cursor={{ fill: theme === 'light' ? 'rgba(15,23,42,.04)' : 'rgba(255,255,255,.04)' }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={64}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className={styles.typeTabs}>
          {TYPE_TABS.map((t) => (
            <button
              key={t.value}
              className={`${styles.typeTab} ${activeType === t.value ? styles.typeTabActive : ''}`}
              onClick={() => setActiveType(t.value)}
            >
              <t.Icon /> {t.label}
            </button>
          ))}
        </div>

        <div className={styles.filtersRow}>
          <div className={styles.filters}>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`${styles.filterBtn} ${activeFilter === f.value ? styles.filterBtnActive : ''}`}
                onClick={() => setActiveFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className={styles.tableSearchWrap}>
            <FiSearch />
            <input placeholder="Search by name or email..." value={tableSearch} onChange={(e) => { setTableSearch(e.target.value); setPage(1); }} />
          </div>
        </div>

        <div className={styles.tableWrap}>
          {status === 'loading' && (
            <div className={styles.loading}><span className={styles.spinnerLg} /></div>
          )}

          {status !== 'loading' && filteredLeads.length === 0 && (
            <div className={styles.empty}>No requests match this filter.</div>
          )}

          {status !== 'loading' && pagedLeads.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Contact</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pagedLeads.map((lead) => (
                  <tr key={lead.id} onClick={() => { setSelected(lead); setContactBy(''); }} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className={styles.nameCell}>{lead.full_name}</div>
                      {lead.fund_org && <div className={styles.subCell}>{lead.fund_org}</div>}
                    </td>
                    <td>
                      {lead.lead_type === 'invest'
                        ? 'Investment'
                        : lead.meeting_type === 'vc'
                        ? 'VC meeting'
                        : 'Physical meeting'}
                    </td>
                    <td>
                      <div>{lead.email}</div>
                      {lead.phone && <div className={styles.subCell}>{lead.phone}</div>}
                    </td>
                    <td>
                      {lead.amount ? <div className={styles.nameCell}>{formatTransaction(Number(lead.amount), lead.currency || 'INR', Number(lead.amount_inr_equivalent ?? lead.amount))}</div> : <span className={styles.subCell}>—</span>}
                    </td>
                    <td><StatusBadge status={lead.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {status !== 'loading' && filteredLeads.length > 0 && (
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          )}
        </div>
      </div>

      <Drawer open={!!selected} onClose={() => setSelected(null)} title="Request details">
        {selected && (
          <>
            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}><FiUser style={{ marginRight: 6 }} />Name</div>
              <div className={drawerStyles.detailValue}>{selected.full_name}</div>
              {selected.fund_org && <div className={adminPanelStyles.subCell}>{selected.fund_org}</div>}
            </div>
            <div className={drawerStyles.detailGrid}>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Type</div>
                <div className={drawerStyles.detailValue}>
                  {selected.lead_type === 'invest' ? 'Investment' : selected.meeting_type === 'vc' ? 'VC meeting' : 'Physical meeting'}
                </div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Amount</div>
                <div className={drawerStyles.detailValue}>{selected.amount ? formatTransaction(Number(selected.amount), selected.currency || 'INR', Number(selected.amount_inr_equivalent ?? selected.amount)) : '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Email</div>
                <div className={drawerStyles.detailValue}>{selected.email}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Phone</div>
                <div className={drawerStyles.detailValue}>{selected.phone || '—'}</div>
              </div>
            </div>
            {selected.message && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}><FiFileText style={{ marginRight: 4 }} />Details</div>
                {selected.message.split(' | ').filter(Boolean).map((part, idx) => (
                  <div key={idx} className={adminPanelStyles.subCell}>{part}</div>
                ))}
              </div>
            )}
            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}>Current status</div>
              <StatusBadge status={selected.status} />
            </div>
            {selected.status === 'confirmed' && selected.contact_by && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>We'll contact by</div>
                <div className={drawerStyles.detailValue}>{new Date(selected.contact_by).toLocaleString()}</div>
              </div>
            )}
            {selected.status === 'pending' && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Set "contact by" before confirming</div>
                <input type="datetime-local" className={adminPanelStyles.filterBtn} style={{ width: '100%', padding: '10px 12px', color: 'var(--cream)' }} value={contactBy} onChange={(e) => setContactBy(e.target.value)} />
              </div>
            )}
          </>
        )}
        {selected && selected.status === 'pending' && (
          <div className={drawerStyles.footer} style={{ position: 'sticky', bottom: 0, background: 'var(--panel)', margin: '24px -24px -24px' }}>
            <button className={adminPanelStyles.actionBtn} onClick={handleConfirm}>Confirm</button>
            <button className={adminPanelStyles.actionBtnReject} onClick={handleReject}>Reject</button>
          </div>
        )}
        {selected && selected.status === 'confirmed' && /Portal:\s*Portal [ABC]/i.test(selected.message || '') && (
          <div className={drawerStyles.footer} style={{ position: 'sticky', bottom: 0, background: 'var(--panel)', margin: '24px -24px -24px', fontSize: 13, opacity: .85 }}>
            Confirming a lead does not create a Portal investment. Once the payment is received, create it from
            Portal Investments, or let the user deposit through the Portal option and approve it in Funds.
          </div>
        )}
      </Drawer>
    </AdminShell>
  );
};

export default AdminDashboard;